/**
 * AVS-4485: the per-dealer 3D host slot (`streamHosts[pseudonym]`). With the VOIP election on, the
 * master tab hosts the 3D stream (AVS-4482); this slot lets another tab of the same dealer host it
 * instead, so a hidden master hands the 3D host to the tab the dealer works in while camera and mic
 * stay on the master. Storage-free, like `voipClaim.ts`.
 *
 * Every write bumps `gen`, so ordering is the voipMasters rule: higher gen wins, an equal gen goes to
 * the lower tabId, and a holder beats a tombstone. A delayed echo of the same holder (an older `away` flip) therefore always loses.
 * A release writes a tombstone `{ tabId: '' }`; read the holder only through `streamHostTabId`.
 */
import { voipMasterTabId, type TVoipMasterSlot } from './voipClaim';

export type TStreamHostSlot = { tabId: string; gen?: number; away?: boolean };

/** The tab holding the host slot, or `undefined` for a missing slot or a tombstone. */
export const streamHostTabId = (slot: TStreamHostSlot | undefined): string | undefined => slot?.tabId || undefined;

/** The tab that hosts 3D for the dealer: the occupied host slot, else the VOIP master. */
export const effectiveHostSlotId = (
  hostSlot: TStreamHostSlot | undefined,
  masterSlot: TVoipMasterSlot | undefined
): string | undefined => streamHostTabId(hostSlot) ?? voipMasterTabId(masterSlot);

/** A tombstone sorts after every holder, so a release never wins an equal-gen tie against a claim. */
const tieKey = (slot: TStreamHostSlot): string => slot.tabId || '\uffff';

const localWins = (local: TStreamHostSlot, incoming: TStreamHostSlot): boolean => {
  const localGen = local.gen ?? 0;
  const incomingGen = incoming.gen ?? 0;
  return localGen > incomingGen || (localGen === incomingGen && tieKey(local) < tieKey(incoming));
};

/**
 * Gen-ordered merge of an incoming `streamHosts` map into the local one. A local slot the incoming map
 * omits survives: slots are never deleted (a release is a tombstone), so an omission is an older
 * writer, not a departure.
 */
export function mergeStreamHosts(
  local: Record<string, TStreamHostSlot> | undefined,
  incoming: Record<string, TStreamHostSlot>
): Record<string, TStreamHostSlot> {
  const merged: Record<string, TStreamHostSlot> = { ...incoming };
  Object.entries(local ?? {}).forEach(([pseudonym, localSlot]) => {
    const incomingSlot = incoming[pseudonym];
    if (!incomingSlot || localWins(localSlot, incomingSlot)) {
      merged[pseudonym] = localSlot;
    }
  });
  return merged;
}

/** True when both maps hold the same slots. */
export function sameStreamHosts(a: Record<string, TStreamHostSlot>, b: Record<string, TStreamHostSlot>): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every(
    (key) =>
      b[key] !== undefined && a[key].tabId === b[key].tabId && a[key].gen === b[key].gen && a[key].away === b[key].away
  );
}
